import React, { useState, useEffect } from "react";
import { ManagerList } from "./ManagerList";
import ListWrapper from "../../../common/management/ListWrapper";
import ListContentWrapper from "../../../common/management/ListContentWrapper";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getAdminUserTotalPages,
  getAdminUserTotalEntries,
  getAdminUsers,
  getJikeiManager,
  addJikeiAccount,
} from "../../../redux/reducers/adminUsers";
import { getAuthToken } from "../../../redux/reducers/auth";

import { genDisplayCount, getTermOptions } from "../../../utils/GeneralUtils";
import { CategoryState } from "../../../utils/CategoryState";
import { RequestState } from "../../../utils/RequestState";
import { TermType, useGetTerm } from "../../../utils/useTerm";
import Modal from "../../../common/management/Modal";
import { JikeiAddUserForm } from "../../../common/management/JikeiAddUserForm";

type Props = {
  currentPage: number;
  perView: number;
};

const ManagerListContainer: React.FC<Props> = ({ currentPage, perView }) => {
  const dispatch = useAppDispatch();
  const managers = useAppSelector(getAdminUsers);
  const totalPage = useAppSelector(getAdminUserTotalPages);
  const entries = useAppSelector(getAdminUserTotalEntries);
  const auth_token = useAppSelector(getAuthToken);

  const schools = useGetTerm(TermType.Schools, undefined, -1).termData.entries;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (auth_token) {
      setIsLoading(true);

      const promise = dispatch(
        getJikeiManager({
          auth_token: auth_token,
          params: {
            page: currentPage,
            per: perView,
          },
        })
      );

      promise.finally(() => setIsLoading(false));

      return () => {
        promise.abort();
      };
    }
  }, [currentPage, perView]);

  // Modal
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [school, setSchool] = useState<string>("");
  const [requestState, setRequestState] = useState(RequestState.Waiting);
  const [categoryState, setCategoryState] = useState<CategoryState>(
    CategoryState.None
  );
  const [error, setError] = useState<string>("");

  const showModal = (category: CategoryState, target?: string) => {
    if (target) {
      setName(target);
    }
    setCategoryState(category);
  };

  const closeModal = () => {
    setCategoryState(CategoryState.None);
    setRequestState(RequestState.Waiting);
    setName("");
    setError("");
  };

  const headText = () => {
    switch (categoryState) {
      case CategoryState.Add:
        switch (requestState) {
          case RequestState.Waiting:
            return "管理者を追加";
          case RequestState.Success:
            return "管理者の追加が完了しました";
          case RequestState.Failed:
            return "管理者の追加に失敗しました。";
          default:
            return "";
        }
      default:
        return "";
    }
  };

  const genModalBody = () => {
    switch (categoryState) {
      case CategoryState.Add:
        switch (requestState) {
          case RequestState.Waiting:
            return (
              <JikeiAddUserForm
                setName={setName}
                setEmail={setEmail}
                setSchool={setSchool}
                schoolOptions={getTermOptions(schools)}
                isManager={true}
              />
            );
          case RequestState.Success:
            return `${name} を追加しました。`;
          default:
            return "";
        }
      default:
        return "";
    }
  };

  const handleAddManager = async () => {
    if (auth_token && name && email && school) {
      setRequestState(RequestState.Doing);
      const res = await dispatch(
        addJikeiAccount({
          auth_token: auth_token,
          name: name,
          email: email,
          school: school,
          is_manager: true,
        })
      );

      if (res.payload === true) {
        setError("");
        setRequestState(RequestState.Success);

        if (auth_token) {
          dispatch(
            getJikeiManager({
              auth_token: auth_token,
              params: {
                page: currentPage,
                per: perView,
              },
            })
          );
        }
      } else {
        setRequestState(RequestState.Failed);
        setError(`${name} の追加に失敗しました。`);
      }
    } else {
      setError("すべての項目を入力してください。");
    }
  };

  return (
    <ListWrapper
      pageTitle={"管理者一覧"}
      btnTitle="管理者を追加"
      handler={() => showModal(CategoryState.Add)}
    >
      {categoryState === CategoryState.Add ? (
        <Modal
          closeModal={closeModal}
          handler={handleAddManager}
          categoryState={categoryState}
          requestState={requestState}
          headText={headText()}
          buttonText={requestState === RequestState.Waiting ? "送信する" : ""}
          error={error}
        >
          {genModalBody()}
        </Modal>
      ) : null}
      <ListContentWrapper
        isLoading={isLoading}
        displayCount={genDisplayCount(currentPage, totalPage, perView, entries)}
        totalEntries={entries}
        currentPage={currentPage}
        totalPage={totalPage}
      >
        <ManagerList managers={managers} />
      </ListContentWrapper>
    </ListWrapper>
  );
};

export default ManagerListContainer;
